import { useEffect, useState } from 'react';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';

import { browserLogger } from '@widget/utils/datadog';
import { useDebounce } from '@toolbox/hooks';
import { parsePlaceDetails } from '@toolbox/components/AddressSuggestField/utils/parsePlaceDetails';
import { usePublicRuntimeConfig } from '@widget/core/PublicRuntimeConfigProvider';

import type { AutocompleteFieldOption } from '../types';

export type Location = {
  streetAndNumber: string;
  city: string;
  country: string;
  postalCode: string;
};

export type UseGooglePlacesProps = {
  searchValue: string;
  country: string;
};

export type UseGooglePlacesState = {
  searchResultOptions: AutocompleteFieldOption[];
  loading: boolean;
};

export type UseGooglePlaces = UseGooglePlacesState & {
  getPlaceDetails: (placeId: string) => Promise<Partial<Location>>;
  placesService: google.maps.places.PlacesService | null;
};

export function useGooglePlaces({ searchValue, country }: UseGooglePlacesProps): UseGooglePlaces {
  const { googleMapsApiKey } = usePublicRuntimeConfig();
  const latestSearchValue = useDebounce(searchValue, 300);
  const [state, setState] = useState<UseGooglePlacesState>({
    loading: false,
    searchResultOptions: [],
  });

  useEffect(() => {
    if (!googleMapsApiKey) {
      browserLogger.error(null, 'Google Api Key required');
    }
  }, [googleMapsApiKey]);

  const {
    placesService,
    placePredictions,
    getPlacePredictions,
    autocompleteSessionToken,
    refreshSessionToken,
  } = usePlacesService({
    apiKey: googleMapsApiKey,
    sessionToken: true,
    options: {
      componentRestrictions: {
        country,
      },
      input: latestSearchValue,
    },
  });

  const getDetails = (
    request: google.maps.places.PlaceDetailsRequest,
  ): Promise<google.maps.places.PlaceResult | null> => {
    return new Promise((resolve) => {
      placesService?.getDetails(request, (result, status) => {
        resolve(status === 'OK' ? result : null);
      });
    });
  };

  useEffect(() => {
    if (latestSearchValue) {
      setState((state) => ({
        ...state,
        loading: true,
        searchResultOptions: [],
      }));
      getPlacePredictions({
        input: latestSearchValue,
        sessionToken: autocompleteSessionToken,
      });
    }
  }, [latestSearchValue]);

  useEffect(() => {
    setState({
      loading: false,
      searchResultOptions: placePredictions.map((item) => ({
        value: item.place_id,
        label: item.structured_formatting.main_text,
        secondaryLabel: item.structured_formatting.secondary_text,
      })),
    });
  }, [placePredictions]);

  const getPlaceDetails = async (placeId: string) => {
    const placeDetails = await getDetails({
      placeId: placeId,
      fields: ['address_components'],
      sessionToken: autocompleteSessionToken,
    });
    refreshSessionToken();
    return parsePlaceDetails(placeDetails);
  };

  return { ...state, getPlaceDetails, placesService };
}
